import type {
  PuzzleSessionStart,
  PuzzleMoveResult,
  UserPuzzleStats,
  DailyPuzzleResponse,
  StreakRenewal,
} from '@draughts/shared';
import { api } from './api';
import type { ApiRequestError } from './api';

// Every call goes through `api` (services/api.ts) rather than a bare fetch(): the
// ApiClient owns the Authorization header, the base URL and — crucially — the
// 401 → refresh → retry interceptor. Hand-rolling fetch here meant an expired
// access token turned every puzzle call into a silent failure (empty daily-puzzle
// card, broken puzzle mid-session) while the rest of the app refreshed and recovered.

/**
 * Com'e' andata la richiesta del problema di oggi. Tre esiti, perche' sono tre
 * cose diverse — e confonderle e' costato un difetto vero:
 *
 * - `'vuoto'`  — il server ha risposto **404**: questa variante oggi non ha un
 *                problema. E' un FATTO, e si puo' ricordare per il resto della
 *                giornata.
 * - `'guasto'` — rete assente, server giu', timeout. E' un INCIDENTE: non dice
 *                niente sui problemi di questa variante, quindi non si ricorda
 *                e si riprova.
 *
 * La stessa forma di `EsitoRinnovo` in `services/api.ts`, e per la stessa
 * ragione: li' «credenziali morte» e «rete ballerina» non vanno confuse, qui
 * «non c'e' un problema» e «non sono riuscito a chiederlo».
 */
export type EsitoProblema =
  | { esito: 'ok'; dati: DailyPuzzleResponse }
  | { esito: 'vuoto' }
  | { esito: 'guasto' };

/**
 * Il problema di oggi, SENZA perdere il perche' di un'eventuale assenza.
 *
 * La distinzione non e' inventata qui: il server risponde gia' 404 quando una
 * variante non ha un problema (`handleGetDailyPuzzle`), e `api.ts` la conserva —
 * un 404 diventa un `ApiRequestError` con `status`, la rete assente un
 * `Error(NETWORK_ERROR_CODE)` che di `status` non ne ha. Era solo il
 * `catch { return null }` di `fetchDailyPuzzle` a cancellarla, e con lei la
 * possibilita' di sapere che cosa fosse lecito mettere in cache.
 */
export async function richiediProblemaDelGiorno(variant: string): Promise<EsitoProblema> {
  try {
    return { esito: 'ok', dati: await api.get<DailyPuzzleResponse>(`/puzzles/daily?variant=${variant}`) };
  } catch (err) {
    return (err as ApiRequestError)?.status === 404 ? { esito: 'vuoto' } : { esito: 'guasto' };
  }
}

/**
 * Il guscio storico: `null` per qualunque assenza, comunque motivata.
 *
 * Resta perche' quattro chiamanti non hanno bisogno della distinzione (il link
 * profondo in App.tsx, `DailyPuzzleInvitePage`, `puzzle-store` e
 * `fetchShortcutPuzzle` qui sotto) e nessuno di loro ha un `catch`: farla
 * lanciare vorrebbe dire quattro rifiuti di promise non gestiti. Delega, cosi'
 * l'implementazione della chiamata resta UNA.
 */
export async function fetchDailyPuzzle(variant: string): Promise<DailyPuzzleResponse | null> {
  const esito = await richiediProblemaDelGiorno(variant);
  return esito.esito === 'ok' ? esito.dati : null;
}

/**
 * Escape-hatch puzzle for the dashboard empty state: when the selected variant
 * has no daily puzzle of its own, fetch a broadly-available one — International
 * first (the flagship 10x10 pool), then Italian (the largest 8x8 pool) — so the
 * "try another puzzle" shortcut always lands on something. Deliberately does NOT
 * run for the primary daily fetch: each variant shows its own puzzle.
 */
export async function fetchShortcutPuzzle(): Promise<DailyPuzzleResponse | null> {
  const international = await fetchDailyPuzzle('international');
  if (international) return international;
  return fetchDailyPuzzle('italian');
}

export async function fetchNextPuzzle(variant: string): Promise<PuzzleSessionStart | null> {
  try {
    return await api.get<PuzzleSessionStart>(`/puzzles/next?variant=${variant}`);
  } catch {
    return null;
  }
}

export async function startPuzzle(puzzleId: number, practice = false): Promise<PuzzleSessionStart> {
  return api.post<PuzzleSessionStart>(`/puzzles/${puzzleId}/start`, { practice });
}

export async function submitPuzzleMove(
  puzzleId: number,
  from: number,
  to: number,
  nonce: string,
  /**
   * Le caselle dei pezzi presi: disambiguano fra due linee legali che condividono
   * `(from, to)` e mangiano pezzi diversi — senza di loro il server applicava la
   * prima che capita, e se non era quella giocata il problema non si chiudeva più.
   * Va in fondo alla firma perché `nonce` era già il quarto parametro.
   */
  captures?: number[]
): Promise<PuzzleMoveResult> {
  // Fuori dal corpo quando non ci sono: una mossa quieta spedisce la stessa
  // richiesta di prima, byte per byte.
  return api.post<PuzzleMoveResult>(`/puzzles/${puzzleId}/move`, {
    from,
    to,
    nonce,
    ...(captures?.length ? { captures } : {}),
  });
}

export interface PuzzleGiveUpResult {
  solution: Array<{ from: number; to: number }>;
  ratingChange?: number;
  newRating?: number;
  puzzleRating?: number;
  /** Presente solo quando svelare la soluzione era la PRIMA attività di oggi:
   *  `solution_revealed` conta per lo Streak (streak.service.ts), quindi può
   *  rinnovarlo come un solve. */
  streak?: StreakRenewal;
}

export async function giveUpPuzzle(): Promise<PuzzleGiveUpResult | null> {
  try {
    return await api.post<PuzzleGiveUpResult>('/puzzles/giveup');
  } catch {
    return null;
  }
}

export interface PuzzleHintResult {
  from: number;
}

/** Ask for a hint: the from-square of the next correct move. Forfeits the
 *  rating for the current attempt (server-side). Returns null on failure. */
export async function getPuzzleHint(): Promise<PuzzleHintResult | null> {
  try {
    return await api.post<PuzzleHintResult>('/puzzles/hint');
  } catch {
    return null;
  }
}

export async function fetchPuzzleStats(): Promise<UserPuzzleStats | null> {
  try {
    return await api.get<UserPuzzleStats>('/puzzles/stats');
  } catch {
    return null;
  }
}
